import React from "react";
import { ScrollView, StyleSheet, TouchableOpacity, View } from "react-native";
import algoliasearch from "algoliasearch";
import { PdModal, PdModalCloseButton, PdModalTitle } from "src/components/overlay";
import { tUseModal, useModal } from "src/providers/OverlayProvider";

import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { tModalComponentProps } from "src/redux/modals/types";
import { ALGOLIA_APP_ID, ALGOLIA_BRANDS, ALGOLIA_SEARCH_KEY } from "src/env";
import {
  InstantSearch,
  useHierarchicalMenu,
  useInstantSearch,
  useRefinementList,
  useSortBy,
  useToggleRefinement,
} from "react-instantsearch-hooks";
import { HierarchicalMenuItem } from "instantsearch.js/es/connectors/hierarchical-menu/connectHierarchicalMenu";

import { PdSelect, tPdSelectOption } from "src/components/forms/select";
import { PdText } from "src/components/layout";
import { PdGiftBox } from "src/components/Icons";
import { PdDivider } from "src/components/layout/table";
import { tSvgProps } from "src/components/types";
import { PdCheckbox, PdCheckboxLabel } from "src/components/forms/checkbox";
import { camelToTitle } from "src/util/gFormatting";
import { PdButton, PdButtonLabel } from "src/components/forms/button";

type tBrandIndexUiState = {
  query?: string;
  sortBy: string;
  hierarchicalMenu: {
    "categories.lvl0": string[];
  };
  refinementList: {
    objectID?: string[];
    brandEthos: string[];
  };
  toggle: {
    isExclusive: boolean;
  };
};

type tBrandUiState = {
  [key: string]: tBrandIndexUiState;
};

type tProps = {
  defaultSearchState: tBrandIndexUiState;
};

const searchClient = algoliasearch(ALGOLIA_APP_ID, ALGOLIA_SEARCH_KEY);
const INITIAL_BRAND_UI_STATE = {
  query: "",
  sortBy: ALGOLIA_BRANDS.DEFAULT,
  hierarchicalMenu: { "categories.lvl0": [] },
  refinementList: { objectID: [], brandEthos: [] },
  toggle: { isExclusive: false },
};
const orderOptions = [
  { value: ALGOLIA_BRANDS.DEFAULT, label: "All" },
  { value: ALGOLIA_BRANDS.NEW, label: "New" },
  { value: ALGOLIA_BRANDS.PRICE_DESC, label: "High to Low" },
  { value: ALGOLIA_BRANDS.PRICE_ASC, label: "Low to High" },
];

const SortBy: React.FC = () => {
  const { options, refine, currentRefinement } = useSortBy({ items: orderOptions });

  const handleOnChange = (newOption: tPdSelectOption | null) => {
    if (newOption) refine(newOption.value);
  };

  return (
    <PdSelect
      containerStyle={styles.orderFilter}
      value={options.find((item) => item.value === currentRefinement)}
      onChange={handleOnChange}
      options={options}
      isClearable={false}
    />
  );
};

const HierarchicalCategoryMenuItem: React.FC<{
  leftIcon?: React.ReactElement<tSvgProps>;
  item: HierarchicalMenuItem;
  onPress: (item: HierarchicalMenuItem) => void;
  onPressSubcategory: (item: HierarchicalMenuItem) => void;
}> = ({ leftIcon, item, onPress, onPressSubcategory }) => {
  const handleOnPress = () => {
    onPress?.(item);
  };

  const handleOnPressSubcategory = (subcategoryItem: HierarchicalMenuItem) => {
    onPressSubcategory?.(subcategoryItem);
  };

  return (
    <View>
      <TouchableOpacity onPress={handleOnPress}>
        <View style={[styles.flexRow, styles.alignCenter]}>
          {!!leftIcon && (
            <View style={styles.hierarchicalMenuItemIcon}>
              {React.cloneElement(leftIcon, { color: kColors.brandDark, style: leftIcon.props.style })}
            </View>
          )}
          <PdText style={[styles.colorBrand, styles.textBase, item.isRefined ? styles.fontBold : styles.fontMedium]}>
            {item.label}
          </PdText>
          <PdText style={[styles.colorBrand, styles.textBase, styles.fontLight]}>{` (${item.count})`}</PdText>
        </View>
      </TouchableOpacity>
      {item.data && (
        <View style={styles.pl3}>
          {item.data.map((subItem) => {
            return (
              <TouchableOpacity
                key={subItem.value}
                onPress={() => {
                  handleOnPressSubcategory(subItem);
                }}
              >
                <View style={[styles.flexRow, styles.alignCenter, styles.justifyBetween, styles.mt4]}>
                  <PdText
                    style={[
                      styles.colorBrand,
                      styles.textSm,
                      subItem.isRefined ? styles.fontSemibold : styles.fontLight,
                    ]}
                  >
                    {subItem.label}
                  </PdText>
                  <PdText style={[styles.colorBrand, styles.textSm, styles.fontLight]}>{subItem.count}</PdText>
                </View>
              </TouchableOpacity>
            );
          })}
        </View>
      )}
    </View>
  );
};

const HierarchicalCategoriesMenu: React.FC<{ onPressSubcategory?: (item: HierarchicalMenuItem) => void }> = ({
  onPressSubcategory,
}) => {
  const { items, refine } = useHierarchicalMenu({
    attributes: ["categories.lvl0", "categories.lvl1"],
    separator: " - ",
    showParentLevel: true,
    limit: 100,
  });

  if (items.length === 0) {
    return null;
  }

  const handleOnPress = (item: HierarchicalMenuItem) => {
    refine(item.value);
  };

  const handleOnSubcategoryPress = (item: HierarchicalMenuItem) => {
    refine(item.value);
    onPressSubcategory?.(item);
  };

  const maybeHolidayMenu = items.find((item) => item.label === "Holiday");

  return (
    <>
      {maybeHolidayMenu && (
        <>
          <HierarchicalCategoryMenuItem
            leftIcon={<PdGiftBox color={kColors.brandDarkRed} style={styles.giftIcon} />}
            item={maybeHolidayMenu}
            onPress={handleOnPress}
            onPressSubcategory={handleOnSubcategoryPress}
          />
          <PdDivider style={styles.my2} />
        </>
      )}
      {items
        .filter((item) => item.label !== "Holiday")
        .map((item) => {
          return (
            <React.Fragment key={item.label}>
              <HierarchicalCategoryMenuItem
                item={item}
                onPress={handleOnPress}
                onPressSubcategory={handleOnSubcategoryPress}
              />
              <PdDivider style={styles.my2} />
            </React.Fragment>
          );
        })}
    </>
  );
};

const BrandEthosCheckboxes: React.FC = () => {
  const { items, refine } = useRefinementList({ attribute: "brandEthos" });

  if (items.length === 0) {
    return null;
  }

  return (
    <>
      {items.map((item) => {
        return (
          <PdCheckbox
            key={item.label}
            value={item.isRefined}
            style={[styles.my1, styles.wFull]}
            onPress={() => refine(item.value)}
          >
            <PdCheckboxLabel style={[styles.textSm, styles.capitalize]}>{camelToTitle(item.label)}</PdCheckboxLabel>
            <PdText style={styles.mlAuto}>{item.count}</PdText>
          </PdCheckbox>
        );
      })}
    </>
  );
};

const ExclusiveCheckbox: React.FC = () => {
  const { value, refine } = useToggleRefinement({ attribute: "isExclusive" });

  const handleOnPress = () => {
    refine({ isRefined: value.isRefined });
  };

  if (value.count === null) {
    return null;
  }

  return (
    <PdCheckbox value={value.isRefined} onPress={handleOnPress} style={[styles.my1, styles.wFull]}>
      <PdCheckboxLabel style={[styles.textSm, styles.capitalize]}>Exclusive</PdCheckboxLabel>
      <PdText style={styles.mlAuto}>{value.onFacetValue.count}</PdText>
    </PdCheckbox>
  );
};

const FilterAndSortButton: React.FC<{ onPress: (value: tBrandIndexUiState) => void }> = ({ onPress }) => {
  const { indexUiState } = useInstantSearch<tBrandUiState>();

  const handleOnPress = () => {
    onPress(indexUiState);
  };

  return (
    <PdButton onPress={handleOnPress} isFullWidth style={styles.mt4}>
      <PdButtonLabel>Filter & Sort</PdButtonLabel>
    </PdButton>
  );
};

const ShopMobileFiltersModal: React.FC<tModalComponentProps<tProps, tBrandIndexUiState>> = ({
  onClose,
  onResolve,
  defaultSearchState,
}) => {
  const scrollViewRef = React.useRef<ScrollView>(null);

  const handleOnClose = () => {
    onClose();
  };

  const handleOnPressClear = () => {
    onResolve(INITIAL_BRAND_UI_STATE);
  };

  const scrollToEthosFilter = () => {
    scrollViewRef.current?.scrollToEnd();
  };

  return (
    <PdModal onClose={handleOnClose} style={styles.modal}>
      <PdModalCloseButton onPress={handleOnClose} />
      <ScrollView ref={scrollViewRef} contentContainerStyle={styles.modalBody}>
        <PdModalTitle style={styles.mb4}>Filter & Sort</PdModalTitle>
        <InstantSearch
          searchClient={searchClient}
          initialUiState={{ [ALGOLIA_BRANDS.DEFAULT]: defaultSearchState }}
          indexName={ALGOLIA_BRANDS.DEFAULT}
        >
          <PdText style={[styles.textBase, styles.wFull, styles.mb4]}>Sort</PdText>
          <SortBy />
          <View style={[styles.mb4, styles.wFull]}>
            <PdText style={[styles.textBase, styles.wFull, styles.mb4]}>Filter By Category</PdText>
            <HierarchicalCategoriesMenu onPressSubcategory={scrollToEthosFilter} />
          </View>
          <PdText style={[styles.textBase, styles.wFull, styles.mb4]}>Filter By Ethos</PdText>
          <BrandEthosCheckboxes />
          <ExclusiveCheckbox />
          <FilterAndSortButton onPress={onResolve} />
          <PdButton variant="outline" onPress={handleOnPressClear} isFullWidth style={styles.mt4}>
            <PdButtonLabel>Clear</PdButtonLabel>
          </PdButton>
        </InstantSearch>
      </ScrollView>
    </PdModal>
  );
};

const useShopMobileFiltersModal = (): tUseModal<tProps, tBrandIndexUiState> => {
  return useModal(ShopMobileFiltersModal);
};

export default useShopMobileFiltersModal;

const styles = StyleSheet.create({
  alignCenter: {
    alignItems: "center",
  },
  capitalize: {
    textTransform: "capitalize",
  },
  colorBrand: {
    color: kColors.brandDark,
  },
  flexRow: {
    flexDirection: "row",
  },
  fontBold: {
    fontWeight: "700",
  },
  fontLight: {
    fontWeight: "300",
  },
  fontMedium: {
    fontWeight: "500",
  },
  fontSemibold: {
    fontWeight: "600",
  },
  giftIcon: {
    height: 20,
  },
  hierarchicalMenuItemIcon: {
    alignItems: "center",
    justifyContent: "center",
    marginRight: 10,
  },
  justifyBetween: {
    justifyContent: "space-between",
  },
  mb4: {
    marginBottom: 4 * kStyleConst.kSpacing,
  },
  mlAuto: {
    marginLeft: "auto",
  },
  modal: {
    marginHorizontal: 8 * kStyleConst.kSpacing,
    maxWidth: 400,
    width: "100%",
  },
  modalBody: {
    alignItems: "flex-start",
    backgroundColor: kColors.lightBrand,
    paddingHorizontal: 6 * kStyleConst.kSpacing,
    paddingVertical: 8 * kStyleConst.kSpacing,
  },
  mt4: {
    marginTop: 4 * kStyleConst.kSpacing,
  },
  my1: {
    marginVertical: 1 * kStyleConst.kSpacing,
  },
  my2: {
    marginVertical: 2 * kStyleConst.kSpacing,
  },
  orderFilter: {
    borderBottomWidth: 1,
    borderWidth: 0,
    marginBottom: 40,
    paddingLeft: 0,
    width: "100%",
    zIndex: 1,
  },
  pl3: {
    paddingLeft: 3 * kStyleConst.kSpacing,
  },
  textBase: {
    fontSize: 16,
  },
  textSm: {
    fontSize: 14,
  },
  wFull: {
    width: "100%",
  },
});
